import { z } from 'zod';
import { baseComponentSchema, imageCropSchema, buttonSchema } from '../component-base.schemas';

// Team component schemas
export const teamMemberSchema = z.object({
  id: z.string().uuid('Invalid team member ID').optional(),
  name: z
    .string()
    .min(1, 'Name must not be empty')
    .max(100, 'Name must be less than 100 characters')
    .optional(),
  position: z
    .string()
    .min(1, 'Position must not be empty')
    .max(150, 'Position must be less than 150 characters')
    .optional(),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  imageUrl: z.string().url('Invalid image URL').optional(),
  imageAlt: z.string().max(200, 'Image alt text must be less than 200 characters').optional(),

  // Image upload metadata
  mediaId: z.string().uuid().optional(),
  s3Key: z.string().optional(),
  cloudfrontUrl: z.string().url().optional(),

  // Image crop data
  crop: imageCropSchema,

  // Social links validation
  socialLinks: z
    .object({
      linkedin: z.string().url('Invalid LinkedIn URL').optional(),
      twitter: z.string().url('Invalid Twitter URL').optional(),
      email: z.string().email('Invalid email address').optional(),
      website: z.string().url('Invalid website URL').optional(),
    })
    .optional(),

  order: z.number().int().min(0).optional(),
});

// Team custom colors schema - allows overriding theme colors per component instance
export const teamCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    nameColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    roleColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const teamComponentSchema = baseComponentSchema.extend({
  type: z.literal('team'),
  data: z.object({
    // Header content
    title: z.string().max(200, 'Title must be less than 200 characters').optional(),
    subtitle: z.string().max(300, 'Subtitle must be less than 300 characters').optional(),
    description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),

    // Team members (at least 1 member required)
    members: z
      .array(teamMemberSchema)
      .min(1, 'At least one team member is required')
      .max(50, 'Maximum 50 team members allowed'),

    // Layout options
    layout: z.enum(['grid', 'list', 'carousel', 'masonry']).default('grid'),
    columns: z.enum(['1', '2', '3', '4', '5', '6']).default('3'),
    alignment: z.enum(['left', 'center', 'right']).default('center'),

    // Member card styling
    cardStyle: z
      .enum(['default', 'minimal', 'hover-effect', 'bordered', 'rounded'])
      .default('default'),
    showPhoto: z.boolean().default(true),
    showPosition: z.boolean().default(true),
    showBio: z.boolean().default(true),
    showSocialLinks: z.boolean().default(true),
    imageShape: z.enum(['square', 'circle', 'rounded-square']).default('circle'),
    imageSize: z.enum(['small', 'medium', 'large']).default('medium'),

    // DEPRECATED: Use customColors.background instead (kept for backward compatibility)
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    // DEPRECATED: Use customColors.text instead (kept for backward compatibility)
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    // DEPRECATED: Use customColors.cardBackground instead (kept for backward compatibility)
    cardBackgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    // DEPRECATED: Use customColors.cardText instead (kept for backward compatibility)
    cardTextColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    // DEPRECATED: Use customColors.roleColor instead (kept for backward compatibility)
    accentColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),

    // Spacing and layout
    spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),
    cardShadow: z.boolean().default(true),
    cardBorder: z.boolean().default(false),

    // Animation and interaction
    hoverEffect: z.enum(['none', 'lift', 'zoom', 'fade']).default('none'),
    animationDelay: z.enum(['none', 'stagger']).default('none'),

    // NEW: Component-specific custom colors that override theme colors
    customColors: teamCustomColorsSchema,

    // Optional call-to-action buttons
    buttons: z.array(buttonSchema).optional(),
  }),
});

// Type exports
export type TeamMember = z.infer<typeof teamMemberSchema>;
export type TeamComponent = z.infer<typeof teamComponentSchema>;
